import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import {
  HowItWorks,
  StatusRow,
  MetricCard,
  CheckItem,
  FakeSelect,
  ProgressBar,
  Tag,
} from "@/components/HowItWorks";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  ShoppingCart,
  ArrowRight,
  ArrowUpRight,
  CreditCard,
  TrendingUp,
  Palette,
  Settings,
  RefreshCw,
  Users,
  CalendarDays,
  MapPin,
  Search,
  BarChart3,
  Gauge,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveAssetUrl } from "@/lib/media";

import { DesignReviewPreview } from "@/components/illustrations/DesignReviewPreview";

import shopifyLogo from "@/assets/partners/shopify.svg";
import klaviyoLogo from "@/assets/partners/klaviyo.svg";
import stripeLogo from "@/assets/partners/stripe.svg";
import mailchimpLogo from "@/assets/partners/mailchimp.svg";
import hubspotLogo from "@/assets/partners/hubspot.svg";
import wooLogo from "@/assets/partners/woocommerce.svg";
import weglotLogo from "@/assets/partners/weglot.svg";

const partners = [
  { name: "Shopify", logo: shopifyLogo },
  { name: "Klaviyo", logo: klaviyoLogo },
  { name: "Stripe", logo: stripeLogo },
  { name: "Mailchimp", logo: mailchimpLogo },
  { name: "HubSpot", logo: hubspotLogo },
  { name: "WooCommerce", logo: wooLogo },
  { name: "Weglot", logo: weglotLogo },
];

const highlights = [
  {
    icon: ShoppingCart,
    title: "Store Design & Development",
    description:
      "Custom-built Shopify stores designed to convert, from theme customisation to fully bespoke builds.",
  },
  {
    icon: RefreshCw,
    title: "Migrations & Integrations",
    description:
      "Seamless migrations to Shopify from any platform, preserving your data, SEO, and customer experience.",
  },
  {
    icon: TrendingUp,
    title: "Growth & Optimisation",
    description:
      "Data-driven CRO, SEO, and retention strategies that turn visitors into loyal customers.",
  },
];

const processSteps = [
  {
    title: "Discovery & Strategy",
    description:
      "We analyse your business, audience, and goals to define the right ecommerce strategy and platform approach.",
    preview: (
      <div className="space-y-3">
        <div className="bg-card rounded-xl p-4 shadow-[0_1px_2px_rgba(10,13,20,0.03),0_0_0_1px_rgba(23,23,23,0.04)]">
          <p className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
            <motion.span
              className="inline-flex"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            >
              <Search className="h-3 w-3" />
            </motion.span>
            Business Audit
          </p>
          <div className="space-y-2.5">
            <CheckItem label="Revenue model & pricing" delay={0.05} tickDelay={0.4} />
            <CheckItem label="Customer segments & personas" delay={0.1} tickDelay={1.0} />
            <CheckItem label="Competitor analysis" delay={0.15} tickDelay={1.6} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FakeSelect label="Platform" value="Shopify Plus" logo={shopifyLogo} delay={0.2} />
          <FakeSelect label="Region" value="UAE & GCC" icon={MapPin} delay={0.25} />
        </div>
      </div>
    ),
  },
  {
    title: "Design & UX",
    description:
      "We design conversion-focused, brand-aligned storefronts that look great and guide shoppers to purchase.",
    preview: <DesignReviewPreview />,
  },
  {
    title: "Development & Integration",
    description:
      "We build your store on Shopify with custom features, payment gateways, and third-party integrations.",
    preview: (
      <div className="space-y-2">
        <StatusRow label="Shopify" status="Connected" connected logo={shopifyLogo} delay={0.05} />
        <StatusRow label="Stripe" status="Connected" connected logo={stripeLogo} delay={0.1} />
        <StatusRow label="Klaviyo" status="Connected" connected logo={klaviyoLogo} delay={0.15} />
        <StatusRow label="HubSpot" status="Pending" logo={hubspotLogo} delay={0.2} />
        <ProgressBar label="Integration Progress" percent={75} delay={0.25} icon={Settings} />
      </div>
    ),
  },
  {
    title: "Launch & Growth",
    description:
      "We launch your store, set up analytics, and provide ongoing CRO, SEO, and retention support to scale.",
    preview: (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <MetricCard label="Conversion" value="3.2%" trend="up" icon={TrendingUp} delay={0.05} />
          <MetricCard label="AOV" value="$142" trend="up" icon={CreditCard} delay={0.1} />
          <MetricCard label="Sessions" value="12.4k" trend="up" icon={BarChart3} delay={0.15} />
        </div>
        <div className="bg-card rounded-xl p-4 shadow-[0_1px_2px_rgba(10,13,20,0.03),0_0_0_1px_rgba(23,23,23,0.04)] space-y-3">
          <ProgressBar label="SEO Score" percent={87} delay={0.2} icon={Search} />
          <ProgressBar label="Page Speed" percent={94} color="bg-emerald-500" delay={0.25} icon={Gauge} />
        </div>
      </div>
    ),
  },
];

const services = [
  {
    icon: ShoppingCart,
    title: "Shopify Store Design & Development",
    description:
      "Custom-built Shopify stores designed to convert, from theme customisation to fully bespoke brand builds.",
    slug: "shopify-store-design-development",
  },
  {
    icon: Settings,
    title: "Shopify Plus Solutions",
    description:
      "Enterprise-grade ecommerce with advanced automation, custom checkout, and scalable architecture.",
    slug: "shopify-plus-solutions",
  },
  {
    icon: Palette,
    title: "Ecommerce UX/UI Design",
    description:
      "Conversion-focused design balancing aesthetics with usability to drive engagement and sales.",
    slug: "ecommerce-ux-ui-design",
  },
  {
    icon: RefreshCw,
    title: "Store Migrations",
    description:
      "Seamless migrations to Shopify from any platform, preserving your data, SEO, and customer experience.",
    slug: "store-migrations",
  },
  {
    icon: CreditCard,
    title: "Payment & Logistics Integrations",
    description:
      "Connect payment gateways, shipping providers, ERPs, and fulfilment for a streamlined operation.",
    slug: "payment-logistics-integrations",
  },
  {
    icon: TrendingUp,
    title: "Ecommerce Optimisation & Growth",
    description:
      "Data-driven CRO, SEO, and retention strategies that turn visitors into loyal customers.",
    slug: "ecommerce-optimisation-growth",
  },
];

const integrations = [
  {
    name: "Shopify",
    logo: shopifyLogo,
    description:
      "The world's leading ecommerce platform for building and scaling online stores.",
  },
  {
    name: "Klaviyo",
    logo: klaviyoLogo,
    description:
      "Email and SMS marketing automation designed specifically for ecommerce brands.",
  },
  {
    name: "Stripe",
    logo: stripeLogo,
    description:
      "Secure, flexible payment processing for online and in-person transactions.",
  },
  {
    name: "HubSpot",
    logo: hubspotLogo,
    description:
      "CRM and marketing platform to manage customer relationships and drive growth.",
  },
];

const faqs = [
  {
    q: "What ecommerce platforms do you build on?",
    a: "We specialise in Shopify and Shopify Plus, but also work with WooCommerce for specific use cases. Shopify is our primary recommendation for most businesses due to its scalability, ecosystem, and ease of use.",
  },
  {
    q: "How long does a Shopify store build typically take?",
    a: "A standard Shopify store takes 4\u20138 weeks from discovery to launch. Shopify Plus or heavily customised builds can take 8\u201312 weeks depending on complexity, integrations, and content readiness.",
  },
  {
    q: "Can you migrate my existing store to Shopify?",
    a: "Yes. We handle full migrations from WooCommerce, Magento, BigCommerce, and other platforms. We preserve your product data, customer records, order history, SEO rankings, and URL structure.",
  },
  {
    q: "Do you provide ongoing support after launch?",
    a: "Absolutely. We offer retainer packages for ongoing optimisation, CRO, new feature development, and technical support. We treat every store as a living product, not a one-time project.",
  },
  {
    q: "What\u2019s included in ecommerce optimisation?",
    a: "Our optimisation services include conversion rate optimisation (CRO), site speed improvements, A/B testing, SEO audits, email/SMS strategy, and analytics setup to drive measurable growth.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const EcommercePage = () => {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const { canonical, hreflang } = buildHreflang("/ecommerce");
  useCmsPageSEO({
    title:
      lang === "ar"
        ? "أبرز وكالة تجارة إلكترونية في الكويت | وافاي"
        : "Leading eCommerce Agency in Kuwait | Wavai",
    description:
      lang === "ar"
        ? "وكالة الكويت الرائدة في التجارة الإلكترونية. تصمّم وافاي وتطوّر متاجر شوبيفاي وووكومرس مهندسة للبيع — في الكويت ودول الخليج."
        : "Kuwait's leading eCommerce agency. Wavai designs, builds and scales Shopify and WooCommerce stores engineered to sell — in Kuwait and the GCC.",
    keywords:
      "ecommerce agency Kuwait, Shopify Kuwait, WooCommerce Kuwait, KNET integration, online store Kuwait",
    canonical,
    hreflang,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Ecommerce Services",
      provider: {
        "@type": "Organization",
        name: "Wavai",
        url: "https://www.wavai.ae",
      },
      description:
        "End-to-end Shopify and ecommerce services: store design, migrations, payments, and growth optimization.",
      areaServed: ["UAE", "Kuwait", "GCC"],
    },
  });

  const { data: works } = useQuery({
    queryKey: ["ecommerce-work"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work")
        .select(
          "title, slug, short_description, featured_image, desktop_screenshot, industry, platforms"
        )
        .eq("status", "published")
        .contains("platforms", ["Shopify"])
        .order("sort_order", { ascending: true })
        .limit(4);
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* ------------------------------------------------------------------ */}
        {/* HERO                                                               */}
        {/* ------------------------------------------------------------------ */}
        <section className="pt-36 pb-16 lg:pt-48 lg:pb-24">
          <div className="container px-6">
            <div className="max-w-3xl mx-auto text-center">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-6"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={0}
              >
                {t("Ecommerce")}
              </motion.span>

              <motion.h1
                className="text-4xl sm:text-5xl lg:text-[3.5rem] font-display font-bold leading-tight mb-6"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={1}
              >
                {t("Kuwait's Leading eCommerce Development Agency")}
              </motion.h1>

              <motion.p
                className="text-base lg:text-lg text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={2}
              >
                {t(
                  "Wavai designs, builds and scales online stores engineered to sell. From Shopify to WooCommerce, we're the team Kuwait's fastest-growing brands trust with their revenue engine — KNET, Tap, MyFatoorah and Arabic RTL all built in."
                )}
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center gap-4 [&>*]:w-full [&>*]:sm:w-auto"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={3}
              >
                <Button variant="hero" size="xl" asChild>
                  <Link to={localePath("/start-a-project")}>
                    {t("Start a Project")} <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
                <Button variant="heroOutline" size="xl" asChild>
                  <Link to={localePath("/contact")}>{t("Book a Call")}</Link>
                </Button>
              </motion.div>
            </div>

            {/* Partner logos */}
            <motion.div
              className="flex flex-wrap items-center justify-center gap-8 lg:gap-12 mt-14 opacity-40"
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={4}
            >
              {partners.map((p) => (
                <img
                  key={p.name}
                  src={p.logo}
                  alt={p.name}
                  className="h-6 sm:h-7 w-auto"
                  loading="lazy"
                />
              ))}
            </motion.div>

            {/* 3 Highlight cards */}
            <div className="grid md:grid-cols-3 gap-6 mt-16">
              {highlights.map((h, i) => (
                <motion.div
                  key={h.title}
                  className="bg-secondary/50 rounded-2xl p-8 flex flex-col gap-8"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                >
                  <div className="w-12 h-12 rounded-xl bg-card flex items-center justify-center shadow-card border border-border">
                    <h.icon className="h-6 w-6 text-foreground" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-sans font-medium text-lg mb-2">
                      {t(h.title)}
                    </h3>
                    <p className="text-base text-muted-foreground leading-relaxed text-balance">
                      {t(h.description)}
                    </p>
                  </div>
                  <Link
                    to={localePath("/ecommerce")}
                    className="mt-auto inline-flex items-center gap-1.5 text-base text-muted-foreground hover:text-foreground transition-colors font-medium"
                  >
                    {t("Learn more")}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* HOW IT WORKS                                                       */}
        {/* ------------------------------------------------------------------ */}
        <HowItWorks
          steps={processSteps}
          heading={t("From idea to live store, fast")}
          subtitle={t(
            "A clear, structured process that takes your store from concept to launch without friction."
          )}
          trustText={t("Trusted by leading ecommerce brands across the GCC.")}
          t={t}
        />

        {/* ------------------------------------------------------------------ */}
        {/* SERVICES                                                           */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24 bg-secondary/30">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-background text-sm text-muted-foreground font-medium mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                {t("What We Offer")}
              </motion.span>
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                {t("Ecommerce Services")}
              </motion.h2>
              <motion.p
                className="text-base text-muted-foreground leading-relaxed"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={2}
              >
                {t(
                  "Everything you need to launch and grow a successful online store, powered by Shopify."
                )}
              </motion.p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-border">
              {services.map((service, i) => (
                <motion.div
                  key={service.slug}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="border-b border-border"
                >
                  <Link
                    to={localePath(`/ecommerce/${service.slug}`)}
                    className="group block p-8 h-full rounded-none hover:rounded-2xl hover:bg-card hover:shadow-[0_8px_30px_-12px_hsl(var(--foreground)/0.12)] transition-[background-color,border-radius,box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5"
                  >
                    <div className="w-11 h-11 rounded-xl bg-primary/8 flex items-center justify-center mb-6 transition-colors duration-200 group-hover:bg-primary/15">
                      <service.icon
                        className="h-5 w-5 text-primary"
                        strokeWidth={1.5}
                      />
                    </div>
                    <h3 className="font-sans font-medium text-lg mb-2 group-hover:text-primary transition-colors duration-200">
                      {t(service.title)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(service.description)}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* INTEGRATIONS                                                       */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24">
          <div className="container px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
              {/* Left */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                <span className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4">
                  {t("Apps & Integrations")}
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight mb-4">
                  {t("Built with the best tools in ecommerce")}
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed mb-6">
                  {t(
                    "Connect your favourite tools and simplify your workflows effortlessly."
                  )}
                </p>
                <Link
                  to={localePath("/ecommerce/integrations-directory")}
                  className="inline-flex items-center gap-1.5 text-base text-primary font-medium hover:underline"
                >
                  {t("See all integrations")}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>

              {/* Right - integration list */}
              <div className="flex flex-col gap-6">
                {integrations.map((item, i) => (
                  <motion.div
                    key={item.name}
                    className="flex gap-5 items-start"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    custom={i}
                  >
                    <div className="w-16 h-16 rounded-2xl border border-border bg-card flex items-center justify-center shrink-0 shadow-card">
                      <img
                        src={item.logo}
                        alt={item.name}
                        className="h-8 w-8 object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <h3 className="font-sans font-medium text-lg mb-1">
                        {item.name}
                      </h3>
                      <p className="text-base text-muted-foreground leading-relaxed">
                        {t(item.description)}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* WORK SHOWCASE                                                      */}
        {/* ------------------------------------------------------------------ */}
        {works && works.length > 0 && (
          <section className="py-16 lg:py-32 bg-foreground text-background">
            <div className="container px-6">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={0}
                >
                  <p className="text-sm uppercase tracking-[0.25em] text-primary font-medium mb-4">
                    {t("Portfolio")}
                  </p>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-background">
                    {t("Selected")}{" "}
                    <span className="text-gradient">{t("Work")}</span>
                  </h2>
                </motion.div>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={1}
                >
                  <Link
                    to={localePath("/work")}
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                  >
                    {t("View all projects")} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </motion.div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
                {works.map((work, i) => {
                  const img = resolveAssetUrl(
                    work.desktop_screenshot || work.featured_image
                  );
                  return (
                    <motion.div
                      key={work.slug}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, margin: "-60px" }}
                      variants={fadeUp}
                      custom={i}
                    >
                      <Link
                        to={localePath(`/work/${work.slug}`)}
                        className="group block"
                      >
                        {img && (
                          <div className="aspect-[16/10] overflow-hidden rounded-xl bg-background/5 mb-5">
                            <img
                              src={img}
                              alt={work.title}
                              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-sans font-semibold text-base lg:text-[20px] text-background group-hover:text-primary transition-colors duration-200">
                              {work.title}
                            </h3>
                            {work.industry && (
                              <p className="text-sm text-background/50 mt-1">
                                {work.industry}
                              </p>
                            )}
                          </div>
                          <ArrowUpRight className="h-5 w-5 text-background/30 group-hover:text-primary flex-shrink-0 mt-1 transition-colors duration-200" />
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* FAQ                                                                */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                {t("FAQ")}
              </motion.span>
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                {t("Frequently asked questions")}
              </motion.h2>
            </div>

            <div className="max-w-[700px] mx-auto">
              <Accordion type="single" collapsible>
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-base">
                      {t(faq.q)}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                      {t(faq.a)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* CTA                                                                */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                {t("Get Started")}
              </motion.span>
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                {t("Ready to grow your online store?")}
              </motion.h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <motion.div
                className="bg-secondary/50 rounded-2xl p-10 lg:p-12 flex flex-col gap-8"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                <Users className="h-8 w-8 text-foreground" strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-2xl font-bold mb-2">
                    {t("Start a project")}
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed">
                    {t(
                      "Tell us about your ecommerce goals and get a tailored proposal within 24 hours."
                    )}
                  </p>
                </div>
                <Button variant="hero" size="lg" className="w-fit" asChild>
                  <Link to={localePath("/start-a-project")}>
                    {t("Get a Free Quote")}
                    <ArrowUpRight className="h-4 w-4 opacity-60" />
                  </Link>
                </Button>
              </motion.div>

              <motion.div
                className="bg-primary rounded-2xl p-10 lg:p-12 flex flex-col gap-8"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                <CalendarDays
                  className="h-8 w-8 text-white"
                  strokeWidth={1.5}
                />
                <div>
                  <h3 className="font-display text-2xl font-bold mb-2 text-white">
                    {t("Book a free consultation")}
                  </h3>
                  <p className="text-base text-white/70 leading-relaxed">
                    {t(
                      "Chat with an ecommerce expert to see how we can help scale your store."
                    )}
                  </p>
                </div>
                <Button
                  size="lg"
                  className="w-fit border-2 border-white/30 bg-transparent text-white hover:bg-white/10"
                  asChild
                >
                  <Link to={localePath("/contact")}>
                    {t("Book a Call")}
                    <ArrowUpRight className="h-4 w-4 opacity-60" />
                  </Link>
                </Button>
              </motion.div>
            </div>

            {/* Partner strip */}
            <motion.div
              className="mt-16 flex flex-col items-center gap-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
            >
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70 font-medium">
                {t("Our partners")}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-10 opacity-40">
                {partners.slice(0, 5).map((p) => (
                  <img
                    key={p.name}
                    src={p.logo}
                    alt={p.name}
                    className="h-5 sm:h-6 w-auto"
                    loading="lazy"
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EcommercePage;
